// src/components/ai/AiInsightPanel.js
import React, { useState } from 'react';
import AiButton from './AiButton';
import AiResultCard from './AiResultCard';
import {
  generateProgressNarrative,
  generateNextStepExplanation,
  generateRetentionRiskNarrative,
  normalizeAiInsightResult,
} from '../../utils/clientAiInsights';
import { buildClientSignalsFromWorkspace } from '../../utils/clientSignals';

// Each entry: insightType matches normalizeAiInsightResult case keys.
// mapToCardResult maps the normalized shape to { text, basedOn, confidence }
// that AiResultCard expects.
const INSIGHT_CONFIG = [
  {
    key: 'progress',
    label: 'Progress Narrative',
    triggerLabel: 'Generate Progress Summary',
    fn: generateProgressNarrative,
    insightType: 'progressNarrative',
    mapToCardResult: (r) => ({ text: r.narrative, basedOn: r.basedOn }),
  },
  {
    key: 'nextStep',
    label: 'Next-Step Explanation',
    triggerLabel: 'Explain Next Step',
    fn: generateNextStepExplanation,
    insightType: 'nextStep',
    mapToCardResult: (r) => ({ text: r.explanation, confidence: r.confidence, basedOn: r.basedOn }),
  },
  {
    key: 'retention',
    label: 'Retention Risk',
    triggerLabel: 'Analyse Retention Risk',
    fn: generateRetentionRiskNarrative,
    insightType: 'retentionNarrative',
    mapToCardResult: (r) => ({ text: r.narrative, basedOn: [] }),
  },
];

function buildEmptyInsightState() {
  return { progress: null, nextStep: null, retention: null };
}

function buildEmptyLoadingState() {
  return { progress: false, nextStep: false, retention: false };
}

function buildEmptyErrorState() {
  return { progress: null, nextStep: null, retention: null };
}

/**
 * Renders three independently-triggerable AI insight cards for ClientProfilePage.
 *
 * Props:
 *   workspace    — result of buildClientWorkspace() — must already be loaded
 *   orgId        — string — required for signal assembly
 *   services     — array — passed through to signals
 *   coachContext — string — optional instructor notes to include in AI payload;
 *                           typically comes from a QuickCoachNotesBox on the parent page
 */
function AiInsightPanel({ workspace, orgId, services = [], coachContext = '' }) {
  const [results, setResults] = useState(buildEmptyInsightState);
  const [loading, setLoading] = useState(buildEmptyLoadingState);
  const [errors, setErrors] = useState(buildEmptyErrorState);

  const handleGenerate = async (key, fn, insightType, mapToCardResult) => {
    setLoading((current) => ({ ...current, [key]: true }));
    setErrors((current) => ({ ...current, [key]: null }));
    try {
      const signals = buildClientSignalsFromWorkspace(workspace, orgId, { services });
      const raw = await fn(signals, { instructorContext: coachContext });
      const normalized = normalizeAiInsightResult(insightType, raw);
      setResults((current) => ({ ...current, [key]: mapToCardResult(normalized) }));
    } catch (err) {
      setErrors((current) => ({ ...current, [key]: err?.message || 'Could not generate AI insight.' }));
    } finally {
      setLoading((current) => ({ ...current, [key]: false }));
    }
  };

  return (
    <div className="space-y-4">
      {INSIGHT_CONFIG.map(({ key, label, triggerLabel, fn, insightType, mapToCardResult }) => {
        const result = results[key];
        const isLoading = loading[key];
        const error = errors[key];
        const hasResult = result && result.text;

        return (
          <div key={key} className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm font-semibold text-slate-900">{label}</p>
              <AiButton
                onClick={() => handleGenerate(key, fn, insightType, mapToCardResult)}
                loading={isLoading}
                label={hasResult ? 'Regenerate' : triggerLabel}
                ghost={hasResult}
              />
            </div>

            {(isLoading || error || hasResult) ? (
              <div className="mt-3">
                <AiResultCard
                  loading={isLoading}
                  error={error}
                  result={result}
                />
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

export default AiInsightPanel;
